import { useState, useEffect } from "react";
import usePlanBody from "../_shared/UserPlanBody/usePlanBody";
import { UserInterface } from "@/shared/model/api.types";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { useDashboard } from "../../../lib/useDashboard";
import { parseUsersInDepartment } from "@/shared/lib/users";
import { useAuth } from "@/features/Auth/lib/useAuth";
import DashboardCard from "../_shared/DashboardCard/DashboardCard";
import LoadingOverlay from "../_shared/LoadingOverlay/LoadingOverlay";
import ErrorOverlay from "../_shared/ErrorOerlay/ErrorOerlay";
import FullMessageOverlay from "../_shared/FullMessageOverlay/FullMessageOverlay";
import { PlanSection } from "../DepartmentsPlan/DepartmentPlanCard";
import DepartmentsPlanPopup from "../DepartmentsPlan/DepartmentsPlanPopup";
import { Button } from "@/shared/components/common/Button/Button";
import css from "./UsersPlan.module.css";
import planCss from "../DepartmentsPlan/DepartmentPlanCard.module.css";
import { useNavigate } from 'react-router-dom';

function UsersPlan() {
  const { selectedDepartment: selectedDepartmentId, dateStart, dateEnd } = useDashboard();
  const departmentId = selectedDepartmentId ? Number(selectedDepartmentId) : null;
  const [selectedUser, setSelectedUser] = useState<UserInterface | null>(null);
  const [optionsUsers, setOptionsUsers] = useState<UserInterface[]>([]);
  const [dealsPopupOpen, setDealsPopupOpen] = useState(false);

  const { loading, error, result } = usePlanBody(
    "userPlan_isPeriod",
    selectedUser?.id ?? null,
    selectedUser ? departmentId ?? undefined : undefined
  );
  const { users } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();

  const openAnalytics = () => {
    if (!selectedUser || !departmentId || !dateStart || !dateEnd) return;
    const query = new URLSearchParams({
      scope: 'employee',
      departmentId: String(departmentId),
      userId: String(selectedUser.id),
      dateStart,
      dateEnd,
    });
    navigate(`/SalePlan/analytics?${query.toString()}`);
  };

  const opportunity = result?.opportunity ?? null;
  const profit = result?.profit ?? null;

  useEffect(() => {
    const filtered = parseUsersInDepartment(users, departmentId).filter(
      (u) => u.id !== user?.bx.bx_user.bx_id
    );
    setOptionsUsers(filtered);
    setSelectedUser((prev) => {
      if (prev && filtered.some((u) => u.id === prev.id)) return prev;
      return filtered[0] ?? null;
    });
  }, [departmentId, users, user]);

  if (!departmentId) return null;

  const hasOpportunity =
    opportunity != null && (opportunity.target ?? 0) > 0;
  const hasProfit = profit != null && (profit.target ?? 0) > 0;
  const deals = result?.deals ?? opportunity?.deals ?? profit?.deals;
  const hasDeals = (deals?.length ?? 0) > 0;

  return (
    <DashboardCard
      col={30}
      rowSpan={12}
      height={600}
      title="План продаж сотрудников"
    >
      <div className={css.container}>
        <div className={css.userChips}>
          {optionsUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`${css.userChip} ${
                selectedUser?.id === u.id ? css.userChipActive : ""
              }`}
              onClick={() => setSelectedUser(u)}
            >
              <img
                src={
                  u.personal_photo ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    u.name + " " + u.last_name
                  )}&background=ddd`
                }
                alt=""
                className={css.userChipAvatar}
              />
              <span className={css.userChipName}>
                {u.last_name} {u.name}
              </span>
            </button>
          ))}
        </div>

        {selectedUser ? (
          <div className={css.planContent}>
            {hasDeals ? (
              <div className={css.planContentHeader}>
                <Button
                  variant="primary"
                  size="s"
                  onClick={openAnalytics}
                  disabled={loading}
                >
                  Аналитика
                </Button>
                <Button
                  variant="secondary"
                  size="s"
                  onClick={() => setDealsPopupOpen(true)}
                  disabled={loading}
                >
                  Детализация
                </Button>
              </div>
            ) : null}
            {loading ? (
              <LoadingOverlay loading />
            ) : error ? (
              <ErrorOverlay error={error} />
            ) : !hasOpportunity && !hasProfit ? (
              <FullMessageOverlay fullMessage="Нет данных" />
            ) : (
              <div className={planCss.card}>
                {hasOpportunity && (
                  <PlanSection
                    title="Оборот"
                    result={opportunity!}
                    showPaid
                    plannedSalesValue={result?.plannedSalesOpportunity ?? null}
                    plannedSalesLabel="Плановые продажи, сумма"
                  />
                )}
                {hasProfit && (
                  <PlanSection
                    title="Валовая прибыль"
                    result={profit!}
                    plannedSalesValue={result?.plannedSalesProfit ?? null}
                    plannedSalesLabel="Плановые продажи, прибыль"
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={css.planPlaceholder}>
            {optionsUsers.length > 0 ? "Выберите сотрудника" : "Нет данных"}
          </div>
        )}
      </div>
      <DepartmentsPlanPopup
        open={dealsPopupOpen}
        onClose={() => setDealsPopupOpen(false)}
        title={
          selectedUser
            ? `${selectedUser.last_name} ${selectedUser.name} — сделки`
            : "Сделки"
        }
        loading={loading}
        deals={deals}
        analytics={selectedUser && dateStart && dateEnd ? {
          dateStart,
          dateEnd,
          scopeLabel: `${selectedUser.last_name} ${selectedUser.name}`,
          opportunityTarget: opportunity?.target ?? 0,
          profitTarget: profit?.target ?? 0,
          opportunityPreparation: opportunity?.valuePreparation ?? 0,
          profitPreparation: profit?.valuePreparation ?? 0,
          plannedSalesOpportunity: result?.plannedSalesOpportunity ?? 0,
          plannedSalesProfit: result?.plannedSalesProfit ?? 0,
          departmentId,
          userId: selectedUser.id,
          scopeKind: 'employee',
        } : undefined}
      />
    </DashboardCard>
  );
}

export default UsersPlan;
